import { api } from './api';

export type UserMemberType =
  | 'admin'
  | 'silk'
  | 'junior'
  | 'pupil'
  | 'door_tenant'
  | 'senior_clerk'
  | 'junior_clerk'
  | 'staff'
  | 'solicitor';

export type UserStatus = 'active' | 'invited' | 'suspended' | 'inactive';

export interface ApiUser {
  id: string;
  chambersId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  memberType: UserMemberType;
  status: UserStatus;
  jobTitle: string | null;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  invitedAt: string | null;
  createdAt: string;
  barristerProfile?: {
    id: string;
    callYear: number | null;
    silkYear: number | null;
    seniority: number;
    availability: string;
    inn: string | null;
  } | null;
}

interface ListUsersResponse {
  data: ApiUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface ListUsersParams {
  search?: string;
  memberType?: UserMemberType;
  status?: UserStatus;
  page?: number;
  limit?: number;
  /** Only return users who can participate in communications (excludes clerks without the
   *  `emails` capability). Pass `true` only; never `false`. */
  communicationsCapable?: boolean;
}

export async function fetchUsers(params: ListUsersParams = {}): Promise<ApiUser[]> {
  const res = await api.get<ListUsersResponse>('/users', { params });
  return res.data.data ?? [];
}

export interface InviteUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  memberType: Exclude<UserMemberType, 'solicitor'>;
  jobTitle?: string;
  phone?: string;
}

export interface InviteUserResult {
  user: ApiUser;
  temporaryPassword: string;
  /** One-click activation link — hand to the invitee when email isn't delivering. */
  activationUrl: string;
}

export async function inviteUser(payload: InviteUserPayload): Promise<InviteUserResult> {
  const res = await api.post<{ data: InviteUserResult }>('/users', payload);
  return res.data.data;
}

export async function updateUserStatus(id: string, status: UserStatus): Promise<ApiUser> {
  const res = await api.patch<{ data: ApiUser }>(`/users/${id}`, { status });
  return res.data.data;
}

/**
 * Chambers admin (or senior clerk, for employed staff) directly sets a member's
 * password. The member is signed out of all sessions and must sign in again
 * with the new password. Governance is enforced server-side.
 */
export async function setUserPassword(id: string, password: string): Promise<void> {
  await api.post(`/users/${id}/set-password`, { password });
}

/**
 * Resend / re-share the activation link for an INVITED member. Rotates the token, emails the link
 * via Resend, and returns the fresh link so the admin can also copy + share it directly.
 */
export async function resendActivation(id: string): Promise<{ email: string; activationUrl: string }> {
  const res = await api.post<{ data: { email: string; activationUrl: string } }>(`/users/${id}/resend-activation`, {});
  return res.data.data;
}
